import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiChevronDown, FiCopy } from 'react-icons/fi';

const LogItem = ({ log }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState({});

  const handleCopy = (key, text) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    setCopySuccess(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopySuccess(prev => ({ ...prev, [key]: false })), 2000);
  };

  const statusColor = log.status >= 400 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
  const methodColor = 'bg-gray-100 text-gray-800';
  
  return (
    <div className="border-b border-gray-200">
      <button onClick={() => setExpanded(!isExpanded)} className="w-full text-left p-2 hover:bg-gray-50 flex items-center justify-between">
        <div className="flex items-center truncate">
          <span className={`px-2 py-0.5 rounded-md text-xs font-mono font-semibold ${methodColor}`}>{log.method || 'SYS'}</span>
          {log.status && <span className={`ml-2 px-2 py-0.5 rounded-md text-xs font-semibold ${statusColor}`}>{log.status}</span>}
          <span className="ml-2 text-sm text-gray-700 truncate">{log.message}</span>
        </div>
        <div className="flex items-center">
            {log.duration && <span className="text-xs text-gray-500 mr-2">{log.duration}ms</span>}
            <FiChevronDown className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {isExpanded && (
        <div className="p-2 bg-gray-50 text-xs font-mono space-y-2">
            {log.request && <div><h5 className="font-bold">Request <button onClick={() => handleCopy('req', log.request)}>{copySuccess.req ? 'Copied' : <FiCopy/>}</button></h5><pre className="bg-white p-1 rounded overflow-auto max-h-32">{JSON.stringify(log.request, null, 2)}</pre></div>}
            {log.response && <div><h5 className="font-bold">Response <button onClick={() => handleCopy('res', log.response)}>{copySuccess.res ? 'Copied' : <FiCopy/>}</button></h5><pre className="bg-white p-1 rounded overflow-auto max-h-32">{JSON.stringify(log.response, null, 2)}</pre></div>}
            {log.error && <div><h5 className="font-bold text-red-600">Error</h5><pre className="bg-red-50 p-1 rounded">{log.error}</pre></div>}
        </div>
      )}
    </div>
  );
};

const filters = ["all", "success", "error", "ncb", "dropbox"];

function ApiLogPanel({ logs }) {
  const [filter, setFilter] = useState('all');

  const filteredLogs = useMemo(() => {
    let reversedLogs = [...logs].reverse();
    if (filter === 'all') return reversedLogs;
    return reversedLogs.filter(log => {
        switch (filter) {
            case 'success': return log.level === 'success' || (log.status && log.status < 400);
            case 'error': return log.level === 'error' || (log.status && log.status >= 400);
            case 'ncb': return log.api === 'ncb';
            case 'dropbox': return log.api === 'dropbox';
            default: return true;
        }
    });
  }, [logs, filter]);

  return (
    <div>
      <h3 className="font-bold text-lg text-gray-800 mb-2">API Logs</h3>
      <div className="flex space-x-1 mb-2">
        {filters.map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 text-xs rounded-md ${filter === f ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{f}</button>
        ))}
      </div>
      <div className="border rounded-md h-[60vh] overflow-y-auto bg-white">
        {filteredLogs.length > 0 ? (
            filteredLogs.map(log => <LogItem key={log.id} log={log} />)
        ) : (
            <p className="p-4 text-sm text-gray-500 text-center">No logs to display.</p>
        )}
      </div>
    </div>
  );
}

export default ApiLogPanel;